/**
 * AiTrainerAuthContext — independent identity for the AI training
 * service. Separate from the main LMS AuthContext because trainers
 * are not LMS users — they're external contributors who self-register
 * to upload exam material.
 *
 * Stores the JWT in localStorage under `ai_trainer_token`, rehydrates
 * on mount by calling /v1/auth/me, exposes login/register/logout/refresh.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { AI_TRAINER_TOKEN_KEY, aiTrainerClient } from '@/lib/aiTrainerClient';

export type TrainerStatus = 'pending' | 'active' | 'disabled';
export type TrainerRole   = 'trainer' | 'admin';

export interface TrainerProfile {
  id: string;
  email: string;
  full_name: string;
  organization: string | null;
  role: TrainerRole;
  status: TrainerStatus;
  created_at: string | null;
  last_login: string | null;
}

interface LoginPayload   { email: string; password: string; }
interface RegisterPayload {
  full_name: string;
  email: string;
  password: string;
  organization?: string;
}

interface Ctx {
  user: TrainerProfile | null;
  /** Has the initial /auth/me call settled? */
  ready: boolean;
  /** True while a request is in flight. */
  busy: boolean;
  error: string | null;
  login:    (p: LoginPayload) => Promise<TrainerProfile>;
  register: (p: RegisterPayload) => Promise<TrainerProfile>;
  refresh:  () => Promise<TrainerProfile | null>;
  logout:   () => void;
  clearError: () => void;
}

const AiTrainerAuthContext = createContext<Ctx | null>(null);

export const useAiTrainerAuth = (): Ctx => {
  const ctx = useContext(AiTrainerAuthContext);
  if (!ctx) throw new Error('useAiTrainerAuth must be used within AiTrainerAuthProvider');
  return ctx;
};

interface AuthResponse {
  success: true;
  token: string;
  expires_in_minutes: number;
  user: TrainerProfile;
}

export const AiTrainerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user,  setUser]  = useState<TrainerProfile | null>(null);
  const [ready, setReady] = useState(false);
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── Boot rehydration ──────────────────────────────────────────── */
  useEffect(() => {
    const token = typeof window !== 'undefined'
      ? window.localStorage.getItem(AI_TRAINER_TOKEN_KEY)
      : null;
    if (!token) { setReady(true); return; }
    (async () => {
      try {
        const r = await aiTrainerClient.get<{ user: TrainerProfile }>('/auth/me');
        setUser(r.data.user);
      } catch {
        window.localStorage.removeItem(AI_TRAINER_TOKEN_KEY);
        setUser(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  /* ── Helpers ───────────────────────────────────────────────────── */
  const persist = (resp: AuthResponse) => {
    window.localStorage.setItem(AI_TRAINER_TOKEN_KEY, resp.token);
    setUser(resp.user);
    return resp.user;
  };

  const wrap = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e: any) {
      const msg = e?.response?.data?.detail
        || e?.response?.data?.message
        || e?.message
        || 'Something went wrong';
      setError(typeof msg === 'string' ? msg : 'Request failed');
      throw e;
    } finally {
      setBusy(false);
    }
  }, []);

  /* ── Actions ───────────────────────────────────────────────────── */
  const login = useCallback((p: LoginPayload) =>
    wrap(async () => {
      const r = await aiTrainerClient.post<AuthResponse>('/auth/login', p);
      return persist(r.data);
    }), [wrap]);

  const register = useCallback((p: RegisterPayload) =>
    wrap(async () => {
      const r = await aiTrainerClient.post<AuthResponse>('/auth/register', p);
      return persist(r.data);
    }), [wrap]);

  const refresh = useCallback(async (): Promise<TrainerProfile | null> => {
    try {
      const r = await aiTrainerClient.post<AuthResponse>('/auth/refresh');
      return persist(r.data);
    } catch {
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    window.localStorage.removeItem(AI_TRAINER_TOKEN_KEY);
    setUser(null);
    setError(null);
  }, []);

  const value: Ctx = useMemo(() => ({
    user, ready, busy, error,
    login, register, refresh, logout,
    clearError: () => setError(null),
  }), [user, ready, busy, error, login, register, refresh, logout]);

  return (
    <AiTrainerAuthContext.Provider value={value}>
      {children}
    </AiTrainerAuthContext.Provider>
  );
};
